import { HttpClient, HttpResponse } from "@angular/common/http";
import { computed, inject, Injectable, Signal } from "@angular/core";
import { PROJECT_NUMBER } from "@features/project/constants/routes.constant";
import { parseToProjectStaffFields, parseToProjectStaffFieldsDto, ProjectStaffFields, ProjectStaffFieldsDto } from "../types/project-staff-data.type";
import { BehaviorSubject, map, Observable, shareReplay, switchMap, tap } from "rxjs";

@Injectable()
export class ProjectStaffDataService {
  private readonly _projectNumber: string = inject(PROJECT_NUMBER);

  private readonly _httpClient: HttpClient = inject(HttpClient);

  // eslint-disable-next-line @unicorn/consistent-function-scoping
  private readonly _basePath: Signal<string> = computed(() => `api/projects/${ this._projectNumber }`);

  private _update$: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);

  public readonly data$: Observable<ProjectStaffFields> = this._update$
    .pipe(
      switchMap(() => this.fetchProjectStaffData()
        .pipe(
          map(parseToProjectStaffFields),
          shareReplay({ bufferSize: 1, refCount: true })
        ))
    );

  private fetchProjectStaffData (): Observable<ProjectStaffFieldsDto> {
    return this._httpClient.get<ProjectStaffFieldsDto>(`${ this._basePath() }/employee-settings`);
  }

  /**
   * Reload data
   */
  public reload (): void {
    this._update$.next();
  }

  /**
   * Resets Data to Original State
   */
  public reset (): Observable<HttpResponse<void>> {
    return this._httpClient.put<undefined>(`${ this._basePath() }/project-settings/reset`, {}, { observe: "response" })
      .pipe(
        tap({
          finalize: () => {
            this._update$.next();
          }
        })
      );
  }

  public update (projectStaffData: ProjectStaffFields): Observable<HttpResponse<void>> {
    const parsedStaffData: ProjectStaffFieldsDto = parseToProjectStaffFieldsDto(projectStaffData);

    return this._httpClient.put<undefined>(`${ this._basePath() }/employee-settings`, parsedStaffData, { observe: "response" })
      .pipe(
        tap({
          finalize: () => {
            this._update$.next();
          }
        })
      );
  }
}
